import {Draw, Snap} from 'ol/interaction';
import {Vector as VectorLayer} from 'ol/layer';
import {Vector as VectorSource} from 'ol/source';
import { DRAW_FEATURE } from '../../utils/styles/styles';
import Edit, { EditEventType } from '../../utils/interactions/Edit';
import Event from 'ol/events/Event';
import MapBrowserEventType from 'ol/MapBrowserEventType';

export const DrawFeaturesEventType = {
  DRAW_START : 'drawstart',
  DRAW_END : 'drawend',
  SELECTED_POINT: EditEventType.SELECTED_POINT,
  UNSELECTED_POINT: EditEventType.UNSELECTED_POINT,
  MODIFYSTART: 'modifystart',
  MODIFYEND: 'modifyend'
};

/**
 * @typedef {Object} DrawFeaturesEventOptions
 * @property {import("ol/Feature").default} [feature] The feature being drawn.
 * @property {Array<number>} [coordinate] The coordinates.
 * @property {module: ol/MapBrowserEvent} [mapBrowserEvent] mapBrowserEvent.
 */

export class DrawFeaturesEvent extends Event {
  /**
     * @param {DrawFeaturesEventType} type Type.
     * @param {DrawFeaturesEventOptions} options The feature drawn.
     */
  constructor(type, options = {}) {
    super(type);
        
    /**
         * The feature being drawn.
         * @type {import("ol/Feature").default}
         * @api
         */
    this.feature = options.feature;
        
    this.coordinate = options.coordinate;

    this.mapBrowserEvent = options.mapBrowserEvent;
  }
}

/**
 * @typedef {Object} DrawFeatureOptions
 * @property {import("../../map").default} map The map.
 * @property {import("ol/style/Style").Style|Array<import("ol/style/Style").Style>|import("ol/style/Style").StyleFunction} [style] Layer style. Default is DRAW_FEATURE
 * @property {boolean} [modify] Can modify features. Default is true.
 * @property {boolean} [snap] Can sanp points. Default is true.
 * @property {import("ol/style/Style").Style|Array<import("ol/style/Style").Style>|import("ol/style/Style").StyleFunction} [drawStyle] The draw style.
 * @property {boolean} [stopClick] Stop click, singleclick, and doubleclick events from firing during drawing. Default : false.
 * @property {boolean} [multiFeatures] Is multi feature possible. Default : false.
 * @property {import('ol/events/condition').Condition} [finishCondition] A function that indicate whether the drawing can be finished. Not used when drawing POINT or MULTI_POINT geometries.
 */

/**
 * A vector layer which allow us to draw and modify features. 
 * Draw, Edit and Snap interactions can be added to the map.
 */
export default class DrawFeatures extends VectorLayer {
  /**
     * @constructor
     * @param {DrawFeatureOptions} options The options.
     */
  constructor(options = {}) {

    /** 
         * The source where features drawn in layer are stored.
         * @type {VectorSource} 
         */
    const source = new VectorSource();

    super({
      source: source,
      style: options.style || DRAW_FEATURE
    });

    this.map = options.map;

    /**
         * The draw interaction
         * @protected 
         * @type {Draw}
         */
    this.draw_ = null;

    /**
         * The modify interaction
         * @private 
         * @type {Edit}
         */
    this.edit_ = null;

    /**
         * The snap interaction
         * @private 
         * @type {Snap}
         */
    this.snap_ = null;

    /**
         * The draw options
         * @type {import('ol/interaction/Draw').Options}
         */
    this.drawOptions_ = {
      style: options.drawStyle,
      stopClick: options.stopClick,
      finishCondition: options.finishCondition
    };

    /**
         * Is multi fetaure are possible.
         * @private
         * @type {boolean}
         */
    this.multi_ = options.multiFeatures;

    /**
         * Condition to insert vertex on strech edition.
         * @private
         * @type {function(import('ol/MapBrowserPointerEvent').default):boolean}
         */
    this.insertVertexOnEditCondition_ = () => true;

    // If the modify parameter is true or undefined, add modify interaction.
    if(options.modify !== false) {
      this.edit_ = new Edit({
        source: source,
        deleteCondition: evt => evt.type === MapBrowserEventType.LONGPRESS,
        insertVertexCondition: evt => this.insertVertexOnEditCondition_(evt)
      });
      this.edit_.on(EditEventType.SELECTED_POINT, e => {
        this.dispatchEvent(new DrawFeaturesEvent(DrawFeaturesEventType.SELECTED_POINT, {coordinate: e.coordinate}));
      });
      this.edit_.on(EditEventType.UNSELECTED_POINT, e => {
        this.dispatchEvent(new DrawFeaturesEvent(DrawFeaturesEventType.UNSELECTED_POINT, {coordinate: e.coordinate}));
      });
      this.edit_.on(EditEventType.MODIFYSTART, e => {
        this.dispatchEvent(new DrawFeaturesEvent(DrawFeaturesEventType.MODIFYSTART, {feature: e.features.getArray()[0], mapBrowserEvent: e.mapBrowserEvent}));
      });
      this.edit_.on(EditEventType.MODIFYEND, e => {
        this.dispatchEvent(new DrawFeaturesEvent(DrawFeaturesEventType.MODIFYEND, {feature: e.features.getArray()[0], mapBrowserEvent: e.mapBrowserEvent}));
      });
    }

    // If the snap parameter is true or undefined, add modify interaction.
    if(options.snap !== false) {
      this.snap_ = new Snap({source: source, pixelTolerance: 20});
    }
  }

  /**
     * Start the edition of features. It adds or changes interaction to edit feature in the right type.
     * @param {GeometryType} geometryType The geometry type of the edited feature.
     * @param {GeometryFunction} geometryFunction Function that is called when a geometry's coordinates are updated.
     */
  startEdition(geometryType, geometryFunction) {
    this.stop();
    this.addInteractions_(geometryType, geometryFunction);
  }

  /**
     * Stop the edition of feature. It removes all interaction that edit feature.
     */
  stop() {
    this.map.removeInteraction(this.draw_);
    if(this.edit_) {
      this.map.removeInteraction(this.edit_);
    }
    if(this.snap_) {
      this.map.removeInteraction(this.snap_);
    }
  }

  /**
     * Set if insert or not vertex on edit strech.
     * @param {boolean} insert If true, insert vertex on edit strech, else never.
     */
  setVertexInsertionOnEdit(insert) {
    this.insertVertexOnEditCondition_ = () => insert;
  }

  /**
     * Add interactions in the layer.
     * @private
     * @param {GeometryType} geometryType The geometry type.
     * @param {GeometryFunction} [geometryFunction] Function that is called when a geometry's coordinates are updated.
     */
  addInteractions_(geometryType, geometryFunction) {
    this.draw_ = this.createDrawInteraction_(geometryType, geometryFunction);
    this.map.addInteraction(this.draw_);
    if(this.edit_) {
      this.map.addInteraction(this.edit_);
    }
    if(this.snap_) {
      this.map.addInteraction(this.snap_);
    }
  }
    
  /**
     * Create a draw interaction.
     * @private
     * @param {GeometryType} geometryType The geometry type.
     * @param {GeometryFunction} [geometryFunction] Function that is called when a geometry's coordinates are updated.
     * @return {Draw} The created draw interaction.
     */
  createDrawInteraction_(geometryType, geometryFunction) {
    let draw = new Draw({
      ...this.drawOptions_,
      source: this.source,
      type: geometryType,
      geometryFunction: geometryFunction,
    });
    this.lastDrawParams_ = {type: geometryType, function: geometryFunction};
    draw.on(DrawFeaturesEventType.DRAW_START, event => {
      this.isDrawing_ = true;
      this.dispatchEvent(event);
    });
    draw.on(DrawFeaturesEventType.DRAW_END, event => {
      this.isDrawing_ = false;
      this.addFeature_(event.feature, event);
    });
    return draw;
  }

  /**
     * @inheritdoc
     */
  setStyle(style) {
    super.setStyle(style);
    if(this.drawOptions_) {
      this.drawOptions_.style = style;
    }
    if(this.draw_) {
      this.getDrawingFeature().setStyle(style);
      const lastDrawParameters = this.lastDrawParams_;
      if(this.isDrawing_) {
        this.draw_.on(DrawFeaturesEventType.DRAW_END, () => {
          this.startEdition(lastDrawParameters.type, lastDrawParameters.function);
        });
      } else {
        this.startEdition(lastDrawParameters.type, lastDrawParameters.function);
      }
    }
  }

  /**
     * Finish the current drawing.
     */
  finishDrawing() {
    if(this.draw_) {
      this.draw_.finishDrawing();
    }
  }

  /**
     * Get the current feature draw.
     * @return {import('ol').Feature} The current drawn feature 
     */
  getDrawingFeature() {
    return this.draw_.getOverlay().getSource().getFeatures()[0];
  }

  /**
     * Get the drawn feature.
     * @return {import("ol/Feature").default} The drawn feature.
     */
  getFeature() {
    return this.getSource().getFeatures()[0];
  }
    
  /**
     * Add feature in the layer.
     * @param {import("ol/Feature").default} feature The feature to add.
     */
  setFeature(feature) {
    this.addFeature_(feature);
  }

  /**
     * Add a feature in source.
     * @private
     * @param {import("ol/Feature").default} feature The feature to add. 
     * @param {DrawEvent} event The drawend event.
     */
  addFeature_(feature, event) {
    if(!this.multi_) {
      this.getSource().clear();
    }
    this.getSource().addFeature(feature);
    this.dispatchEvent(event === undefined ? DrawFeaturesEventType.DRAW_END : 
      new DrawFeaturesEvent(DrawFeaturesEventType.DRAW_END, event.feature));
  }

  modifyPointCoordiante(coord) {
    this.edit_.setPoint(coord);
  }

  clear() {
    this.getSource().clear();
  }

}